import React, { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";

const empty = {
    name: "",
    title: "",
    tagline: "",
    bio: "",
    location: "",
    email: "",
    phone: "",
    avatar_url: "",
    resume_url: "",
    hero_video_type: "youtube",
    hero_video_url: "",
    hero_poster_url: "",
    available_for_hire: true,
    years_experience: 0,
    projects_completed: 0,
    happy_clients: 0,
    social: { github: "", linkedin: "", discord: "", twitter: "", instagram: "", steam: "", youtube: "", dribbble: "", website: "" },
};

export default function AdminProfile() {
    const [data, setData] = useState(empty);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/profile")
            .then((res) => setData({ ...empty, ...res.data, social: { ...empty.social, ...(res.data.social || {}) } }))
            .finally(() => setLoading(false));
    }, []);

    const update = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };
    const updateSocial = (k) => (e) => setData((s) => ({ ...s, social: { ...s.social, [k]: e.target.value } }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/profile", data);
            toast.success("Profile updated.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="mono-label">Loading…</div>;

    return (
        <div data-testid="admin-profile-page">
            <Header
                title="Profile & Hero"
                subtitle="Edit konten yang tampil di halaman utama: nama, jabatan, video hero, dan tautan sosial."
                action={
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="profile-save"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover disabled:opacity-60"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Save changes
                    </button>
                }
            />

            <div className="grid lg:grid-cols-2 gap-6">
                <Card title="Identity">
                    <Field label="Full name"><input className="ainp" value={data.name} onChange={update("name")} /></Field>
                    <Field label="Title / Role"><input className="ainp" value={data.title} onChange={update("title")} /></Field>
                    <Field label="Tagline"><input className="ainp" value={data.tagline} onChange={update("tagline")} /></Field>
                    <Field label="Bio"><textarea rows={5} className="ainp" value={data.bio} onChange={update("bio")} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Location"><input className="ainp" value={data.location} onChange={update("location")} /></Field>
                        <Field label="Email"><input className="ainp" value={data.email} onChange={update("email")} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Phone"><input className="ainp" value={data.phone} onChange={update("phone")} /></Field>
                        <Field label="Avatar URL"><input className="ainp" value={data.avatar_url} onChange={update("avatar_url")} /></Field>
                    </div>
                </Card>

                <Card title="Hero Video">
                    <Field label="Video source">
                        <select className="ainp" value={data.hero_video_type} onChange={update("hero_video_type")} data-testid="hero-video-type">
                            <option value="youtube">YouTube Embed</option>
                            <option value="file">Uploaded Video URL (mp4)</option>
                            <option value="none">No video (poster image only)</option>
                        </select>
                    </Field>
                    <Field label={data.hero_video_type === "youtube" ? "YouTube URL or embed link" : "Direct video URL (.mp4)"}>
                        <input
                            data-testid="hero-video-url"
                            className="ainp"
                            value={data.hero_video_url}
                            onChange={update("hero_video_url")}
                            placeholder={data.hero_video_type === "youtube" ? "https://youtube.com/watch?v=…" : "https://cdn.example.com/showreel.mp4"}
                        />
                    </Field>
                    <Field label="Poster image URL (fallback)">
                        <input className="ainp" value={data.hero_poster_url} onChange={update("hero_poster_url")} placeholder="https://images.unsplash.com/…" />
                    </Field>

                    <div className="grid grid-cols-3 gap-3 pt-2">
                        <Field label="Years exp."><input type="number" className="ainp" value={data.years_experience} onChange={update("years_experience")} /></Field>
                        <Field label="Projects"><input type="number" className="ainp" value={data.projects_completed} onChange={update("projects_completed")} /></Field>
                        <Field label="Clients"><input type="number" className="ainp" value={data.happy_clients} onChange={update("happy_clients")} /></Field>
                    </div>
                    <label className="flex items-center gap-3 pt-2 cursor-pointer">
                        <input type="checkbox" checked={data.available_for_hire} onChange={update("available_for_hire")} className="w-4 h-4 accent-brand" />
                        <span className="text-sm text-ink-100">Available for new projects</span>
                    </label>
                </Card>

                <Card title="Social links & Community" className="lg:col-span-2">
                    <div className="grid sm:grid-cols-2 gap-3">
                        {[
                            { k: "github", label: "GitHub", ph: "https://github.com/username" },
                            { k: "linkedin", label: "LinkedIn", ph: "https://linkedin.com/in/username" },
                            { k: "discord", label: "Discord", ph: "https://discord.gg/... or username (e.g. skymortis)" },
                            { k: "steam", label: "Steam", ph: "https://steamcommunity.com/id/username" },
                            { k: "twitter", label: "Twitter / X", ph: "https://x.com/username" },
                            { k: "instagram", label: "Instagram", ph: "https://instagram.com/username" },
                            { k: "youtube", label: "YouTube", ph: "https://youtube.com/@channel" },
                            { k: "website", label: "Gaming / Subdomain Site", ph: "https://skymortis.akhmadferdian.com" },
                        ].map(({ k, label, ph }) => (
                            <Field key={k} label={label}>
                                <input className="ainp" value={data.social[k] || ""} onChange={updateSocial(k)} placeholder={ph} />
                            </Field>
                        ))}
                    </div>
                </Card>
            </div>

            <style>{`
                .ainp{width:100%;background:rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:10px 12px;color:#fff;font-size:14px;font-family:inherit}
                .ainp:focus{outline:none;border-color:rgba(255,69,0,0.6);box-shadow:0 0 0 3px rgba(255,69,0,0.1)}
                .ainp::placeholder{color:#555}
                select.ainp{appearance:none;background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'><path d='m6 9 6 6 6-6'/></svg>");background-repeat:no-repeat;background-position:right 12px center;padding-right:32px}
            `}</style>
        </div>
    );
}

export function Card({ title, children, className = "" }) {
    return (
        <section className={`p-6 rounded-xl border border-white/5 bg-ink-800/40 space-y-4 ${className}`}>
            {title && <h3 className="font-display text-lg text-white">{title}</h3>}
            {children}
        </section>
    );
}

export function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mono-label block mb-1.5">{label}</span>
            {children}
        </label>
    );
}
